import { provideToastr } from "ngx-toastr";
import { provideAnimations } from "@angular/platform-browser/animations";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideServiceWorker } from "@angular/service-worker";
import { provideRouter, TitleStrategy, withViewTransitions } from "@angular/router";
import { HttpBackend, HttpClient, provideHttpClient, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { TranslateLoader, TranslateModule, MissingTranslationHandler, MissingTranslationHandlerParams } from "@ngx-translate/core";

import { routes } from "./app.routes";
import { environment } from "../environments/environment";
import { errorInterceptor, loadingInterceptor, offlineInterceptor, TitleService, tokenInterceptor } from "./core";
// Deliberately not re-exported from `core/offline/index.ts` — every consumer imports it by path,
// because the ones that matter (`AuthService`, `errorInterceptor`) are reached during bootstrap where
// a barrel is how `core` ends up half-initialised. Here it makes no difference either way: `./core`
// above and `./app.routes` already pull the barrel in. Kept by path for one rule, not two.
import { CacheLifecycleService } from "./core/offline/cache-lifecycle.service";

const HttpLoaderFactory = (http: HttpBackend) => {
  return new TranslateHttpLoader(new HttpClient(http), "./assets/i18n/", ".json");
};

class CustomMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (params.key.includes("BE_VALIDATION")) return false;
    return params.key;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: TitleStrategy, useClass: TitleService },
    provideAnimations(),
    // provideAnimationsAsync(),
    // Order matters. `offlineInterceptor` runs after `tokenInterceptor`, so it sees the final
    // `DOMAIN_API` url and its cache keys stay stable, and before `loadingInterceptor` /
    // `errorInterceptor`, so a cache hit never flashes the spinner and never toasts.
    provideHttpClient(withInterceptors([tokenInterceptor, offlineInterceptor, loadingInterceptor, errorInterceptor])),
    importProvidersFrom([
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLanguage,
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpBackend],
        },
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomMissingTranslationHandler },
      }),
    ]),
    provideRouter(routes, withViewTransitions()),
    // Before the first request and before `CacheWarmService`, which is the whole point: a cache the
    // previous shift filled, or a previous build filled, must be gone before this session reads or
    // writes anything. The one exception is an IndexedDB that will not open — see
    // `BOOTSTRAP_TIMEOUT_MS`, which trades that ordering for an app that boots at all.
    provideAppInitializer(() => inject(CacheLifecycleService).init()),
    provideToastr({
      toastClass: "ngx-toastr",
      onActivateTick: true,
      maxOpened: 1,
      autoDismiss: true,
    }),
    // Registration is keyed on `environment.production`, not `isDevMode()`. The qc configuration
    // builds with `optimization: false`, so `isDevMode()` is true there and the service worker
    // would silently never register on QC — the one place it most needs testing. Both `qc` and the
    // default (live) environment set `production: true`; only `environment.development.ts` is false,
    // which is what `ng serve` uses.
    provideServiceWorker("ngsw-worker.js", {
      enabled: environment.production,
      registrationStrategy: "registerWhenStable:5000",
    }),
  ],
};
